import type { ToolCallGroupItem } from "src/base-components/agents/ToolCallGroup";
import {
  type AIMessage,
  type HumanMessage,
  type SessionMessagesResponseMessagesInner,
  type ToolMessage,
  ToolType,
} from "src/clients/agent-management";
import { mentionBlockToReferenceMarkdown } from "src/unifiedCopilot/chat/contentBlocks";

/** tool_call_id → the tool message that answered it. */
export const buildToolResultMap = (
  messages: SessionMessagesResponseMessagesInner[],
): Map<string, ToolMessage> =>
  new Map(
    messages
      .filter((m): m is { type: "tool" } & ToolMessage => m.type === "tool")
      .map((m) => [m.tool_call_id, m] as const),
  );

type VisibleChatMessage =
  | ({ type: "human" } & HumanMessage)
  | ({ type: "ai" } & AIMessage);

// Tool messages are folded into the AI message that called them.
const visibleChatMessages = (
  messages: SessionMessagesResponseMessagesInner[],
): VisibleChatMessage[] =>
  messages.filter(
    (message): message is VisibleChatMessage =>
      message.type === "ai" ||
      (message.type === "human" && Boolean(message.content_blocks?.length)),
  );

export type ChatTurn = {
  key: string;
  messages: VisibleChatMessage[];
};

/** A turn opens on each human message and absorbs the AI messages after it. */
export const chatTurns = (
  messages: SessionMessagesResponseMessagesInner[],
): ChatTurn[] =>
  visibleChatMessages(messages).reduce<ChatTurn[]>((turns, message, idx) => {
    const openTurn = turns.at(-1);
    if (message.type === "human" || openTurn === undefined) {
      turns.push({
        key: message.id ?? `${message.type}-${idx}`,
        messages: [message],
      });
    } else {
      openTurn.messages.push(message);
    }
    return turns;
  }, []);

// Interim shape: the AI-turn flattening below is a placeholder until the
// upcoming trace-view rework replaces it.
export type TurnItem =
  | { kind: "text"; key: string; text: string }
  | { kind: "tool-thread"; key: string; items: ToolCallGroupItem[] };

type AIContentBlock = NonNullable<AIMessage["content_blocks"]>[number];

type InlineBlock = Extract<AIContentBlock, { type: "text" | "mention" }>;

const isInlineBlock = (block: AIContentBlock): block is InlineBlock =>
  block.type === "text" || block.type === "mention";

const inlineBlockMarkdown = (block: InlineBlock): string =>
  block.type === "text" ? block.text : mentionBlockToReferenceMarkdown(block);

/**
 * Consecutive text and mention blocks merge into one markdown string
 * (mentions serialize to their inline reference-tag form) so pills render
 * mid-sentence; a tool call ends the current string and stands alone. Blocks
 * of unknown kinds degrade to nothing instead of crashing.
 */
const appendAiItem = (
  toolResults: Map<string, ToolMessage>,
  items: ToolCallGroupItem[],
  block: AIContentBlock,
  key: string,
): ToolCallGroupItem[] => {
  if (isInlineBlock(block)) {
    const previous = items.at(-1);
    if (previous?.kind === "text") {
      return [
        ...items.slice(0, -1),
        { ...previous, text: `${previous.text}${inlineBlockMarkdown(block)}` },
      ];
    }
    return [...items, { kind: "text", key, text: inlineBlockMarkdown(block) }];
  }
  if (block.type === "tool_call") {
    // The structured-output tool carries the final answer, not a tool call
    // the user should see.
    if (block.metadata.type === ToolType.STRUCTURED_OUTPUT) {
      return items;
    }
    return [
      ...items,
      {
        kind: "tool",
        toolCall: block,
        toolResult: toolResults.get(block.id) ?? null,
      },
    ];
  }
  return items;
};

/**
 * Flattens a turn's AI messages into one ordered list of text runs and tool
 * calls (with results). Runs never merge across messages.
 */
export const aiFlatItems = (
  turn: ChatTurn,
  toolResults: Map<string, ToolMessage>,
): ToolCallGroupItem[] =>
  turn.messages
    .filter((m): m is { type: "ai" } & AIMessage => m.type === "ai")
    .flatMap((message, msgIdx) => {
      const messageKey = message.id ?? `${turn.key}-ai-${msgIdx}`;
      return (message.content_blocks ?? []).reduce<ToolCallGroupItem[]>(
        (items, block, blockIdx) =>
          appendAiItem(
            toolResults,
            items,
            block,
            `${messageKey}-text-${blockIdx}`,
          ),
        [],
      );
    });

/**
 * Splits a turn into what the user sees: tool calls, plus any text the agent
 * wrote between its first and last tool call, collapse into one thread, while
 * the opening text and the final answer stay outside. While the turn is still
 * running, trailing text stays in the thread too, since it may be narration
 * for a tool call that has not arrived yet.
 */
export const aiTurnItems = (
  turn: ChatTurn,
  toolResults: Map<string, ToolMessage>,
  isInProgress: boolean,
): TurnItem[] => {
  const flat = aiFlatItems(turn, toolResults);
  const firstToolIdx = flat.findIndex((item) => item.kind === "tool");
  const foldEndIdx =
    isInProgress && firstToolIdx !== -1
      ? flat.length
      : flat.findLastIndex((item) => item.kind !== "text");
  const isInThread = (item: ToolCallGroupItem, idx: number) =>
    item.kind === "tool" || (idx > firstToolIdx && idx < foldEndIdx);
  const thread = flat.filter(isInThread);

  return flat.flatMap((item, idx): TurnItem[] => {
    if (idx === firstToolIdx) {
      return [
        { kind: "tool-thread", key: `${turn.key}-thread`, items: thread },
      ];
    }
    return item.kind === "text" && !isInThread(item, idx) ? [item] : [];
  });
};
